import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import {
    AsyncRequestQueue,
    Button,
    Caret,
    ChildrenWrapperProps,
    DashNotificationsModule,
    Image,
    Label,
    Popover,
    useAsync,
    UserNotificationComponent
} from 'UI-Dash-Commons-UI';


const NotificationBellImage = require('./../images/menu/menu_notification.svg');

const UserNotificationsComponent: React.FunctionComponent = () => {
    const queueRef = useRef<AsyncRequestQueue>(new AsyncRequestQueue());
    const queue = queueRef.current;
    const location = useLocation();
    const [pathname, setPathname] = React.useState<string>(location.pathname);
    const pollingInterval = 10 * 60 * 1000; // 10 minutes in milliseconds
    const [isInitialLoadComplete, setIsInitialLoadComplete] = React.useState<boolean>(false);
    const UserNotifications = useAsync({
        asyncFunction: DashNotificationsModule.managers.UserNotificationsManager.loadNotifications,
        initialState: [],
        successCallback: () => {
            if (!isInitialLoadComplete) {
                setIsInitialLoadComplete(true);
            }
        },
        errorCallback: () => {
            queue.clear();
            setTimeout(() => {
                queue.enqueue(() => UserNotifications.execute());
            }, 30000);
        }
    });
    const DismissAllNotifications = useAsync({
        asyncFunction: DashNotificationsModule.managers.UserNotificationsManager.dismissAllNotifications,
    });

    const DismissNotification = useAsync({
        asyncFunction: DashNotificationsModule.managers.UserNotificationsManager.dismissNotification,
    });

    const filteredNotificationsData = UserNotifications.data?.filter((notification) => !notification.isGrowl);

    const noNotifications = !filteredNotificationsData || filteredNotificationsData.length === 0;

    useEffect(() => {
        queue.enqueue(() => UserNotifications.execute());
        const notificationsIntervalId = setInterval(() => {
            queue.enqueue(() => UserNotifications.execute());
        }, pollingInterval);

        return () => {
            queue.clear();
            clearInterval(notificationsIntervalId);
        }
    }, []);

    useEffect(() => {
        if (location.pathname !== pathname) {
            setPathname(location.pathname);
            queue.enqueue(() => UserNotifications.execute());
        }
    }, [location.pathname]);

    const notificationCount = filteredNotificationsData.length > 99 ? '99+' : filteredNotificationsData.length;

    const headerElement = <DropdownHeader>
        <NotificationBell
            src={NotificationBellImage}
            alt={'UserNotifications'}
            width={38}
            height={38}
            style={{ cursor: 'pointer', filter: 'contrast(0.5)' }}
        />
        {
            filteredNotificationsData && filteredNotificationsData.length > 0 &&
            <NotificationCount>{notificationCount}</NotificationCount>
        }
    </DropdownHeader>;

    const onClickDismiss = (notification: DashNotificationsModule.types.NotificationType) => {
        if (!filteredNotificationsData) return;
        UserNotifications.setData(filteredNotificationsData.filter((e) => e.id != notification.id));
        queue.enqueue(() => DismissNotification.execute(notification.id));
    }

    const onClickDismissAll = () => {
        UserNotifications.setData([]);
        queue.enqueue(() => DismissAllNotifications.execute());
    }

    const popover = () => {
        let content;

        if (UserNotifications.error) {
            content = <NoNotificationsLabel text={'Error Loading Notifications'} />;
        }
        else if (noNotifications) {
            content = <NoNotificationsLabel text={'You have no new notifications'} />;
        }
        else if (filteredNotificationsData) {
            content = filteredNotificationsData?.map((notification) => {
                return <UserNotificationComponent
                    key={notification.id}
                    notification={notification}
                    onClickDismiss={onClickDismiss}
                    isDismissButtonEnabled={true}
                />
            })
        }


        return <DropdownChildren>
            <HeaderWrapper>
                <Label text={'Notifications'} />
                <DismissNotificationsButton
                    text={'Dismiss All'}
                    onClick={onClickDismissAll}
                    isDisabled={noNotifications}
                />
            </HeaderWrapper>
            <ContentWrapper>
                {content}
            </ContentWrapper>
            <DropdownCaret />
        </DropdownChildren>
    }


    return (
        <DropdownWrapper>
            {
                isInitialLoadComplete &&
                <Popover
                    HeaderComponent={headerElement}
                    ChildrenComponent={popover()}
                    ChildrenWrapper={DropdownChildrenWrapper}
                    hideOnMouseOutClick={true}
                />
            }
        </DropdownWrapper>
    );
}
    ;

const NotificationBell = styled(Image)``;

export default UserNotificationsComponent;


const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 0 10px;
    border-bottom: 2px solid ${props => props.theme.colors.borderGrayLight};
    min-height: 48px;
`;

const DismissNotificationsButton = styled(Button)`
    font-family: ${props => props.theme.fonts.medium};
    font-size: 14px;
    color: ${props => props.theme.colors.anchorText};

    text-decoration: none;
`;

const ContentWrapper = styled.div`
    min-height: 100px;
    max-height: 450px;
    width: 450px;
    overflow-y: scroll;
`;

const DropdownWrapper = styled.div`
    display: flex;
    align-items: stretch;
    flex-direction: column;

    position: relative;

    margin-top: 6px;
    margin-right: 10px;

    outline: none;
`;

const DropdownCaret = styled(Caret)`
    display: block;

    position: absolute;
    top: -7px;
    right: 4px;

    z-index: 99;
`;

const DropdownHeader = styled.div`
    cursor: pointer;
`;

const DropdownChildren = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;

    height: inherit;
    width: inherit;
`;


const DropdownChildrenWrapper = styled<ChildrenWrapperProps, 'div'>('div')`
    position: absolute;
    top: 42px;
    right: -2px;
    z-index: 100;

    display: none;
    display: ${props => props.isOpen && 'inline-block'};
    outline: none;

    background-color: ${props => props.theme.colors.white};
    border-radius: 3px;

    box-shadow: 0 2px 10px rgba(90, 97, 102, 0.4);

    max-height: 500px;
    width: 450px;
`;


const NotificationCount = styled.div`
    border-radius: 50%; /* Makes the shape a circle */
    background: radial-gradient( 5px -9px, circle, white 8%, red 26px );
    background-color: ${props => props.theme.colors.backgroundDarkRed}; /* Background color */
    color: white;
    height: 15px; 
    min-width: 15px;
    padding: 2px 2px 0 2px;
    text-align: center;
    top: 23px;
    left: 23px;
    font-size: 10px;
    font-weight: bold;
    position: absolute;
    font-family: ${props => props.theme.fonts.regular};
`;

const NoNotificationsLabel = styled(Label)`
    & span {
        margin: auto;
    }

    padding: 50px;
`;